/**
 * responsible to initialize Router where we will render our routes.
 */

import * as PIXI from 'pixi.js-legacy';
import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import IPlayerQueryParams from '../../player/ui/IPlayerQueryParams';
import IEditorQueryParams from '../../editor/ui/IEditorQueryParams';
import IMaticQueryParams from '../../matic/ui/IMaticQueryParams';
import IEncoderQueryParams from '../../encoder/ui/IEncoderQueryParams';
import Logger from '../../common/log/Logger';
import { defaultWideoId, defaultAccessToken } from "../../editor/core/EditorConstants";
import { Suspense } from 'react';

PIXI.utils.skipHello();

const PLAYER_DEFAULTS_QUERY_PARAMS: IPlayerQueryParams = {
  debug: false,
  autoplay: false,
  repeat: false,
  startTime: 0,
  wideoId: defaultWideoId,
  accessToken: defaultAccessToken,
  environment: 'develepe',
  hideControls: false,
  convert: false,
  forceCanvas: true,
  antialias: true,
  replace: null,
  replaceId: null,
  jsonUrl: null,
  superUser: false
};

const EDITOR_DEFAULTS_QUERY_PARAMS: IEditorQueryParams = {
  environment: 'develepe',
  wideoId: defaultWideoId,
  accessToken: defaultAccessToken,
  debug: false,
  autoSave: true,
  new: false,
  superUser: false,
  convert: false,
  width: undefined,
  height: undefined,
  forceCanvas: true,
  antialias: true,
  replace: null,
  replaceId: null
};

const MATIC_DEFAULTS_QUERY_PARAMS: IMaticQueryParams = {
  debug: false,
  autoplay: false,
  repeat: false,
  startTime: 0,
  wideoId: defaultWideoId,
  accessToken: defaultAccessToken,
  environment: 'develepe',
  hideControls: false,
  convert: false,
  forceCanvas: true,
  antialias: true,
  replace: null,
  replaceId: null,
  jsonUrl: null,
  superUser: false,
  redirect: true,
  showCTA: true,
  excludeColors: '',
  export: false
};

const ENCODER_DEFAULTS_QUERY_PARAMS: IEncoderQueryParams = {
  environment: 'develepe',
  wideoId: null,
  accessToken: null,
  encoderID: 'HTML5-Encoder',
  serverName: null,
  apiURL: null,
  pingPort: null,
  imageType: 'jpg',
  fps: 30,
  forceCanvas: true,
  antialias: true,
  frameExtract: 'pixiExtractPixels',
  replace: null,
  replaceId: null,
  jsonUrl: null,
  start: 0,
  end: 0,
  processId: 0,
  s3Key: '',
  s3AccessKey: null,
  s3Bucket: null
};

function initEnv() {
  if (window.location.hostname.indexOf('editordev.wideo.co') >= 0) {
    ENCODER_DEFAULTS_QUERY_PARAMS.environment = 'develepe';
    EDITOR_DEFAULTS_QUERY_PARAMS.environment = 'develepe';
    PLAYER_DEFAULTS_QUERY_PARAMS.environment = 'develepe';
    MATIC_DEFAULTS_QUERY_PARAMS.environment = 'develepe';
  }
  else if (window.location.hostname.indexOf('editor.wideo.co') >= 0) {
    ENCODER_DEFAULTS_QUERY_PARAMS.environment = 'prod';
    EDITOR_DEFAULTS_QUERY_PARAMS.environment = 'prod';
    PLAYER_DEFAULTS_QUERY_PARAMS.environment = 'prod';
    MATIC_DEFAULTS_QUERY_PARAMS.environment = 'prod';
  }
}


function getQueryParams<T>(searchString: string, defaults: T): T {
  const queryParams: T = {} as T;
  const nameValuePairs: string[] = searchString.substr(1).split('&');
  if (searchString !== 'undefined' && searchString !== '') {
    for (const nameValue of nameValuePairs) {
      const [name, value] = nameValue.split('=');

      if (defaults.hasOwnProperty(name)) {
        if (value === 'false') {
          queryParams[name] = false;
        } else if (value === 'true') {
          queryParams[name] = true;
        } else {
          queryParams[name] = value;
        }
      } else {
        Logger.warn('Non existent query param: ' + name + ' = ' + value);
      }
    }

    for (const param in defaults) {
      if (typeof queryParams[param] !== 'undefined') {
        defaults[param] = queryParams[param];
      }
    }

  }

  return defaults;
}

const Main = () => {
  initEnv();
  return (
    <main>
      <Switch>
        <Route
          path="/"
          strict={true}
          exact={true}
          render={(props) => {
            window.location.href = "https://app.wideo.co/account/myprofile";
            return null;
          }
          }
        />
        <Route
          path="/editor"
          strict={true}
          exact={true}
          render={(props) => {
            if (props.location.search) {
              const EditorApp = React.lazy(() => import('../../editor/ui/EditorApp'));
              const queryParams = getQueryParams(props.location.search, EDITOR_DEFAULTS_QUERY_PARAMS);
              return (<Suspense fallback={<></>}><EditorApp
                {...props}
                queryParams={queryParams}
              />
              </Suspense>)

            } else {
              window.location.href = "https://app.wideo.co/account/myprofile";
              return null;
            }
          }}
        />
        <Route
          path="/player"
          strict={true}
          exact={true}
          render={(props) => {
            if (props.location.search) {
              const PlayerApp = React.lazy(() => import('../../player/ui/PlayerApp'));
              return (<Suspense fallback={<></>}><PlayerApp
                {...props}
                queryParams={getQueryParams(props.location.search,
                  PLAYER_DEFAULTS_QUERY_PARAMS)}
              /></Suspense>)
            } else {
              window.location.href = "https://app.wideo.co/account/myprofile";
              return null;
            }
          }}
        />

        <Route
          path="/encoder"
          strict={true}
          exact={true}
          render={(props) => {
            const EncoderApp = React.lazy(() => import('../../encoder/ui/EncoderApp'));
            return (<Suspense fallback={<></>}><EncoderApp
              {...props}
              queryParams={getQueryParams(props.location.search,
                ENCODER_DEFAULTS_QUERY_PARAMS)}
            /></Suspense>)
          }}
        />
        <Route
          path="/matic"
          strict={true}
          exact={true}
          render={(props) => {
            const MaticApp = React.lazy(() => import('../../matic/ui/MaticApp'));
            return (<Suspense fallback={<></>}><MaticApp
              {...props}
              queryParams={getQueryParams(props.location.search,
                MATIC_DEFAULTS_QUERY_PARAMS)}
            /></Suspense>)
          }}
        />
        <Route
          path="/alive"
          strict={true}
          exact={true}
          render={(props) => <span>Alive!</span>
          }
        />
        <Redirect from="/player/" to="/player" />
        <Redirect from="/editor/" to="/editor" />
        <Redirect from="/encoder/" to="/encoder" />
        <Redirect to="/player" />
      </Switch>
    </main>
  );
};

export default Main;



// WEBPACK FOOTER //
// src/opt/atlassian/pipelines/agent/build/src/common/ui/Main.tsx